export const RELAY_SIGNALS = {
  allChanged: 'all.changed',
  unreadChanged: 'unread.changed',
  conversationChanged: 'conversation.changed',
  activeConversationChanged: 'active-conversation.changed',
  casesChanged: 'cases.changed',
} as const

export type RelaySignal = (typeof RELAY_SIGNALS)[keyof typeof RELAY_SIGNALS]
export type RelayRefresh = () => Promise<void> | void

export interface RelayStrategy {
  start: (publish: (signal: RelaySignal) => Promise<void>) => () => void
}

export interface RelayEnvironment {
  isVisible: () => boolean
  isOnline: () => boolean
  subscribe: (listener: () => void) => () => void
}

export const browserRelayEnvironment: RelayEnvironment = {
  isVisible: () => document.visibilityState === 'visible',
  isOnline: () => navigator.onLine,
  subscribe: (listener) => {
    document.addEventListener('visibilitychange', listener)
    window.addEventListener('online', listener)
    window.addEventListener('offline', listener)
    return () => {
      document.removeEventListener('visibilitychange', listener)
      window.removeEventListener('online', listener)
      window.removeEventListener('offline', listener)
    }
  },
}

export class Relay {
  private readonly refreshers = new Map<RelaySignal, Set<RelayRefresh>>()
  private readonly inFlight = new Map<
    RelayRefresh,
    { promise: Promise<void>; pending: boolean }
  >()
  private enabled = false
  private active = false
  private disposed = false
  private stopStrategy: (() => void) | undefined
  private unsubscribeEnvironment: (() => void) | undefined

  constructor(
    private readonly strategy: RelayStrategy,
    private readonly environment: RelayEnvironment = browserRelayEnvironment,
    private readonly onError?: (error: unknown) => void
  ) {}

  subscribe(signal: RelaySignal, refresh: RelayRefresh): () => void {
    const subscribers = this.refreshers.get(signal) ?? new Set<RelayRefresh>()
    subscribers.add(refresh)
    this.refreshers.set(signal, subscribers)
    return () => {
      subscribers.delete(refresh)
      if (subscribers.size === 0) this.refreshers.delete(signal)
    }
  }

  setEnabled(enabled: boolean): void {
    if (this.disposed || this.enabled === enabled) return
    this.enabled = enabled
    if (enabled) {
      this.unsubscribeEnvironment = this.environment.subscribe(() =>
        this.sync()
      )
    } else {
      this.unsubscribeEnvironment?.()
      this.unsubscribeEnvironment = undefined
    }
    this.sync()
  }

  async publish(signal: RelaySignal): Promise<void> {
    if (!this.active) return
    const subscribers = new Set<RelayRefresh>(this.refreshers.get(signal))
    if (signal === RELAY_SIGNALS.allChanged) {
      for (const entries of this.refreshers.values()) {
        for (const refresh of entries) subscribers.add(refresh)
      }
    }
    await Promise.all(
      [...subscribers].map((refresh) => {
        const existing = this.inFlight.get(refresh)
        if (existing) {
          existing.pending = true
          return existing.promise
        }
        const state = { promise: Promise.resolve(), pending: false }
        const running = Promise.resolve()
          .then(async () => {
            do {
              state.pending = false
              try {
                await refresh()
              } catch (error) {
                if (!state.pending || !this.active) throw error
              }
            } while (state.pending && this.active)
          })
          .catch((error: unknown) => {
            this.onError?.(error)
            throw error
          })
          .finally(() => this.inFlight.delete(refresh))
        state.promise = running
        this.inFlight.set(refresh, state)
        return running
      })
    )
  }

  async idle(): Promise<void> {
    await Promise.allSettled(
      [...this.inFlight.values()].map((state) => state.promise)
    )
  }

  dispose(): void {
    this.setEnabled(false)
    this.disposed = true
    this.refreshers.clear()
  }

  private sync(): void {
    const shouldRun =
      this.enabled &&
      this.environment.isVisible() &&
      this.environment.isOnline()
    if (!shouldRun) {
      this.stopStrategy?.()
      this.stopStrategy = undefined
      this.active = false
      return
    }
    if (this.active) return
    this.active = true
    try {
      this.stopStrategy = this.strategy.start((signal) => this.publish(signal))
      void this.publish(RELAY_SIGNALS.allChanged).catch(() => undefined)
    } catch (error) {
      this.active = false
      this.onError?.(error)
    }
  }
}
